import React from 'react';
import { Music2 } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { useNavigate } from 'react-router-dom';
import Comments from './Comments';
import { useMediaQuery } from '@/hooks/use-mobile';
import { useVideoInteraction } from '@/hooks/use-video-interaction';
import { useAuth } from '@/contexts/AuthContext';

interface VideoDetailsProps {
  title: string;
  description: string;
  creator: string;
  views: string;
  music: string;
  isTrending?: boolean;
  isPartnered?: boolean;
  videoId: string;
}

const VideoDetails: React.FC<VideoDetailsProps> = ({
  title,
  description,
  creator,
  views,
  music,
  isTrending,
  isPartnered,
  videoId
}) => {
  const navigate = useNavigate();
  const isMobile = useMediaQuery('(max-width: 768px)');
  const { user } = useAuth();
  const { isLiked } = useVideoInteraction(videoId);

  const handleProfileClick = () => {
    navigate('/profile');
  };

  // Basic content shown to all users
  const basicContent = (
    <div className="space-y-4">
      <h1 className="text-3xl font-bold leading-tight tracking-tight">{title}</h1>
      <p className="text-base text-white/90 leading-relaxed">{description}</p>
    </div>
  );

  // Additional content shown only after liking
  const engagementContent = isLiked ? (
    <div className="space-y-6">
      <div className="flex flex-col gap-3">
        <div className="flex items-center gap-3 text-white/70">
          <div className="flex items-center gap-2">
            <Music2 className="w-4 h-4" />
            <span className="text-xs font-medium">{music}</span>
          </div>
          <div className="w-1 h-1 rounded-full bg-white/30" />
          <span className="text-xs font-medium">{views} views</span>
        </div>

        <div 
          className="flex items-center gap-3 cursor-pointer hover:bg-white/5 p-2 -mx-2 rounded-lg transition-colors"
          onClick={handleProfileClick}
        >
          <Avatar className="h-8 w-8 ring-2 ring-white/10">
            <AvatarImage src="https://github.com/shadcn.png" />
            <AvatarFallback>CC</AvatarFallback>
          </Avatar>
          <span className="text-sm font-medium">{creator}</span>
        </div>

        {(isTrending || isPartnered) && (
          <div className="flex gap-2 flex-wrap pt-2">
            {isTrending && (
              <Badge variant="secondary" className="bg-primary/10 text-primary hover:bg-primary/15 transition-colors">
                🔥 Trending
              </Badge>
            )}
            {isPartnered && (
              <Badge variant="secondary" className="bg-accent/10 text-accent hover:bg-accent/15 transition-colors">
                ⭐️ Partnered
              </Badge>
            )}
          </div>
        )}
      </div>
    </div>
  ) : null;

  if (isMobile) {
    return (
      <div className="fixed bottom-24 left-4 text-white space-y-4 max-w-[70%] z-10">
        {basicContent}
        {engagementContent}
      </div>
    );
  }

  return (
    <div className="fixed top-[200px] right-4 w-[480px] text-white space-y-6 bg-gradient-to-br from-black/60 via-black/50 to-black/60 backdrop-blur-md p-6 rounded-2xl border border-white/10 shadow-2xl">
      {basicContent}
      {engagementContent}

      {isLiked && (
        <div className="pt-6 mt-6 border-t border-white/10">
          <Comments videoId={videoId} isMobile={isMobile} />
        </div>
      )}
    </div>
  );
};

export default VideoDetails;